import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Coins } from "lucide-react";

import { agentApi } from "@/lib/extensions-api";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { formatNumber } from "@/lib/format";
import { toast } from "sonner";

interface Form { points: number; reference: string; }

export function AgentLoadPointsModal({
  agentId, onClose,
}: { agentId: number | null; onClose: () => void }) {
  const qc = useQueryClient();
  const { register, handleSubmit, reset, watch } = useForm<Form>();
  const points = watch("points");

  const load = useMutation({
    mutationFn: (v: Form) => agentApi.admin.loadPoints(agentId!, Number(v.points), v.reference),
    onSuccess: () => {
      toast.success(`Loaded ${formatNumber(Number(points))} points.`);
      qc.invalidateQueries({ queryKey: ["admin-agents"] });
      reset();
      onClose();
    },
  });

  return (
    <Modal open={!!agentId} onClose={onClose} title="Load Points to Agent">
      <form onSubmit={handleSubmit((v) => load.mutate(v))} className="space-y-4">
        <div className="p-3 bg-warning-50 border border-warning-200 rounded-lg text-xs text-warning-800">
          Points loaded here become available for this Agent to distribute.
          Once loaded, the transaction is immutable and audit-logged.
        </div>
        <Input
          label="Points to Load"
          type="number"
          min="1"
          placeholder="10000"
          required
          {...register("points", { required: true, min: 1, valueAsNumber: true })}
        />
        <Input
          label="Reference / Reason"
          placeholder="Weekly allocation, promo, etc."
          {...register("reference")}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} type="button">Cancel</Button>
          <Button type="submit" loading={load.isPending}
                   leftIcon={<Coins className="h-4 w-4" />}>
            Load Points
          </Button>
        </div>
      </form>
    </Modal>
  );
}
